import type { FC } from "react";
import { useState } from "react";
import { ICON_BASE_PATH } from "../config";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useT } from "../../hooks/useT";

interface MaskedAccountNumberProps {
  accountNumber?: string | number | null;
  maskedAccountNumber?: string; // masked version
  maskChar?: string;
  maskGroups?: number;
  maskGroupLength?: number;
  separator?: string;
  visibleDigits?: number;
  maskClassName?: string;
  visibleClassName?: string;
  wrapperClassName?: string;
  showToggle?: boolean;
  toggleIconClassName?: string;
  white?: boolean;
  showCopy?: boolean;
  copyIconClassName?: string;
}

const MaskedAccountNumber: FC<MaskedAccountNumberProps> = ({
  accountNumber,
  maskChar = "*",
  maskGroups = 2,
  maskGroupLength = 4,
  separator = "",
  visibleDigits = 4,
  maskClassName = "tracking-widest relative top-[3px]",
  visibleClassName = "",
  wrapperClassName = "inline-flex items-center",
  showToggle = true,
  white = false,
  showCopy = false,
  copyIconClassName = "gx-icon-d cursor-pointer w-5 h-5",
}) => {
  const t = useT();
  const [show, setShow] = useState(false);
  const { copied, copy } = useCopyToClipboard(String(accountNumber || ""));

  if (!accountNumber || accountNumber === "---") {
    return <span className="text-gray-400 text-sm opacity-70"></span>;
  }

  const acctStr = String(accountNumber);

  const visiblePart =
    acctStr.length > visibleDigits ? acctStr.slice(-visibleDigits) : acctStr;

  const maskPattern = new Array(maskGroups)
    .fill(maskChar.repeat(maskGroupLength))
    .join(separator);

  const getIconName = () => {
    if (show) {
      return white ? "eye-show-white.svg" : "eyeopen.svg";
    }

    return white ? "eye-hide-white.svg" : "eyeclose.svg";
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (accountNumber) {
      copy();
    }
  };

  return (
    <span className={`${wrapperClassName} gap-2`}>
      <span dir="ltr" className="inline-flex items-center">
        {show ? (
          <span className={visibleClassName}>{acctStr}</span>
        ) : (
          <>
            <span className={`inline-block ${maskClassName}`}>
              {maskPattern}
            </span>
            <span className={visibleClassName}>{visiblePart}</span>
          </>
        )}
      </span>

      {showToggle && (
        <img
          src={`${ICON_BASE_PATH}${getIconName()}`}
          alt="toggle visibility"
          className={`gx-icon-d cursor-pointer w-5 h-5 ${
            white ? "h-[20px] w-[20px]" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            setShow(!show);
          }}
        />
      )}

      {showCopy && show && (
        <>
          {copied ? (
            <span className="gx-p10">{t("copied")}</span>
          ) : (
            <img
              src={`${ICON_BASE_PATH}${
                white ? "copy-icon-white.svg" : "copy-icon.svg"
              }`}
              alt="copy"
              className={copyIconClassName}
              onClick={handleCopy}
            />
          )}
        </>
      )}
    </span>
  );
};

export default MaskedAccountNumber;
